import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Option } from 'src/components/SelectBox';
import { CRM_PROVIDER_OPTIONS } from '../components/wizard/WizardContext';
import { useCampaignContext } from './CampaignContext';
import {
  IntegrationKey,
  PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS,
} from 'src/services/integration-app.service';

export enum CrmSyncStep {
  configuration = 'configuration',
  mapping = 'mapping',
  preview = 'preview',
}

export const providerSupportsSegmentSelection = (
  provider: IntegrationKey,
): boolean => {
  return PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS.includes(provider);
};

interface ImportError {
  recordDescription: string;
  message: string;
}

interface CrmSyncContextValue {
  currentStep: CrmSyncStep;
  integratedCrm: Option | null;
  segmentId?: string;
  syncInProgress: boolean;
  importErrors: ImportError[];

  setCurrentStep: (step: CrmSyncStep) => void;
  setIntegratedCrm: (crm: Option | null) => void;
  setSegmentId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSyncInProgress: (inProgress: boolean) => void;
  setImportErrors: Dispatch<SetStateAction<ImportError[]>>;

  handleStepNavigation: (direction: 'back' | 'continue') => void;
  handleConfigurationComplete: () => void;
  clearErrors: () => void;

  navigate: ReturnType<typeof useNavigate>;
}

const CrmSyncContext = createContext<CrmSyncContextValue | undefined>(
  undefined,
);

export const useCrmSync = () => {
  const context = useContext(CrmSyncContext);
  if (context === undefined) {
    throw new Error('useCrmSync must be used within a CrmSyncProvider');
  }
  return context;
};

interface CrmSyncProviderProps {
  children: ReactNode;
}

export const CrmSyncProvider: React.FC<CrmSyncProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { campaign, campaignReferenceId } = useCampaignContext();

  const [currentStep, setCurrentStep] = useState<CrmSyncStep>(
    CrmSyncStep.configuration,
  );
  const [integratedCrm, setIntegratedCrm] = useState<Option | null>(null);
  const [segmentId, setSegmentId] = useState<string | undefined>(undefined);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);

  useEffect(() => {
    if (campaign?.crmCampaignConfig?.provider) {
      const crmOption = CRM_PROVIDER_OPTIONS.find(
        (option) => option.value === campaign?.crmCampaignConfig?.provider,
      );
      if (crmOption) {
        setIntegratedCrm(crmOption);
      }
    }
  }, [campaign]);

  useEffect(() => {
    if (integratedCrm) {
      const supportsSegmentSelection = providerSupportsSegmentSelection(
        integratedCrm.value as IntegrationKey,
      );
      if (
        !supportsSegmentSelection &&
        currentStep === CrmSyncStep.configuration
      ) {
        setCurrentStep(CrmSyncStep.mapping);
      }
    }
  }, [integratedCrm, currentStep]);

  const handleStepNavigation = (direction: 'back' | 'continue') => {
    if (direction === 'back') {
      if (currentStep === CrmSyncStep.preview) {
        setCurrentStep(CrmSyncStep.mapping);
      } else if (currentStep === CrmSyncStep.mapping) {
        const supportsSegmentSelection = integratedCrm
          ? providerSupportsSegmentSelection(
              integratedCrm.value as IntegrationKey,
            )
          : false;
        if (supportsSegmentSelection) {
          setCurrentStep(CrmSyncStep.configuration);
        } else {
          navigate(`/campaigns/${campaignReferenceId}/contacts`);
        }
      } else {
        navigate(`/campaigns/${campaignReferenceId}/contacts`);
      }
    } else {
      if (currentStep === CrmSyncStep.configuration) {
        setCurrentStep(CrmSyncStep.mapping);
      } else if (currentStep === CrmSyncStep.mapping) {
        setCurrentStep(CrmSyncStep.preview);
      }
    }
  };

  const handleConfigurationComplete = () => {
    setCurrentStep(CrmSyncStep.mapping);
  };

  const clearErrors = () => {
    setImportErrors([]);
  };

  const value: CrmSyncContextValue = {
    currentStep,
    integratedCrm,
    segmentId,
    syncInProgress,
    importErrors,

    setCurrentStep,
    setIntegratedCrm,
    setSegmentId,
    setSyncInProgress,
    setImportErrors,

    handleStepNavigation,
    handleConfigurationComplete,
    clearErrors,

    navigate,
  };

  return (
    <CrmSyncContext.Provider value={value}>{children}</CrmSyncContext.Provider>
  );
};

export type { ImportError };
